import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function MatchPage() {
  const { user } = useAuth();
  const [need, setNeed] = useState("");
  const [category, setCategory] = useState("any");
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const runMatch = async () => {
    if (!user) { toast.error("Please log in to use AI Match"); return; }
    if (!need.trim()) { toast.error("Describe what you need help with"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/ai/match", {
        need, category: category === "any" ? null : category,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI match failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16">
      <div className="label-eyebrow text-[#2DD4BF] mb-3">AI Match</div>
      <h1 className="font-display text-4xl md:text-5xl text-[#E8ECF3] leading-tight mb-4">Describe your need. We'll find the wisdom.</h1>
      <p className="text-[#94A3B8] max-w-2xl mb-10">Our AI reads your problem and shortlists three retired experts most likely to help — powered by Claude Sonnet 4.5.</p>

      <div className="bg-[#0F1B33] border border-[#142542] rounded-3xl p-8 space-y-5">
        <Textarea
          rows={5}
          placeholder="e.g., We are a 15-person SaaS at ₹5cr ARR. We're seeing high sales team churn — need someone who has built an outbound org in India in the 2000s to review our comp plans, coach our manager, and help us fix onboarding."
          value={need}
          onChange={(e) => setNeed(e.target.value)}
          className="bg-[#0F1B33] text-base"
          data-testid="match-need-input"
        />
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12 bg-[#0F1B33]" data-testid="match-category-select">
                <SelectValue placeholder="Any category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any category</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={runMatch} disabled={busy}
                  className="h-12 rounded-full bg-[#2DD4BF] hover:bg-[#14B8A6] text-[#05070C] px-8" data-testid="match-run-btn">
            <Sparkles className="h-4 w-4 mr-2" /> {busy ? "Matching…" : "Find my match"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="mt-12">
          <div className="label-eyebrow text-[#2DD4BF] mb-3">AI reasoning</div>
          <p className="text-[#E8ECF3] leading-relaxed mb-8">{result.reasoning}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(result.matches || []).map((m, i) => {
              const p = m.profile;
              if (!p) return null;
              return (
                <div key={m.id} className="bg-[#0F1B33] border border-[#142542] rounded-3xl p-6 hover-lift">
                  <div className="label-eyebrow text-[#2DD4BF]">Match {i + 1}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <img src={p.picture} alt="" className="w-14 h-14 rounded-full object-cover" />
                    <div>
                      <div className="font-display text-lg text-[#E8ECF3]">{p.name}</div>
                      <div className="text-xs text-[#94A3B8]">{p.headline}</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-[#E8ECF3] italic border-l-2 border-[#2DD4BF] pl-3">
                    {m.reason}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" /> {p.avg_rating || "—"}</span>
                    <span className="font-display text-lg">₹{p.hourly_rate}<span className="text-xs text-[#94A3B8]">/hr</span></span>
                  </div>
                  <Link to={`/professionals/${p.user_id}`}>
                    <Button className="w-full mt-4 rounded-full bg-[#2DD4BF] hover:bg-[#14B8A6] text-white" data-testid={`match-view-${p.user_id}`}>
                      View profile
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
